/**
 * Deteção de dia relativo e intervalo de horas em pedidos de rota.
 *
 * Puro, multilingue (pt/en/es/fr/it/de), sem IA nem Maps. Resolve casos como
 * "amanhã às 13h até às 16h" sem confundir com "hoje" nem pedir duração extra.
 */

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

export type RelativeDayWindow = {
  /** true quando o texto indica claramente dia/hora dentro de um único dia. */
  intraday: true;
  /** Desvio em dias relativamente a hoje (0 = hoje, 1 = amanhã, …). */
  dayOffset: number;
  /** Início do intervalo, em minutos desde a meia-noite, quando indicado. */
  startMinutes?: number;
  /** Fim do intervalo, em minutos desde a meia-noite, quando indicado. */
  endMinutes?: number;
  /** Duração calculada em horas (fim − início). */
  durationHours?: number;
  /** A hora final indicada já passou no dia referido. */
  expired: boolean;
};

const DAY_OFFSET_PATTERNS: Array<{ offset: number; re: RegExp }> = [
  { offset: 2, re: /\b(?:depois\s+de\s+amanha|depois\s+de\s+amanhã|after\s+tomorrow|pasado\s+manana|pasado\s+mañana|apres[- ]?demain|dopodomani|ubermorgen)\b/u },
  { offset: 1, re: /\b(?:amanha|amanhã|tomorrow|manana|mañana|demain|domani|morgen)\b/u },
  { offset: 0, re: /\b(?:hoje|today|hoy|aujourd'hui|oggi|heute)\b/u },
];

const TONIGHT_PATTERNS: RegExp[] = [
  /\b(?:esta\s+noite|hoje\s+a\s+noite|tonight|esta\s+noche|ce\s+soir|ce\s+soiree|stasera|heute\s+abend)\b/u,
];

type StartEndMatch = {
  startHour: number;
  startMinute?: number;
  startSuffix?: string;
  endHour: number;
  endMinute?: number;
  endSuffix?: string;
};

function groupsToMatch(groups: Record<string, string | undefined> | undefined): StartEndMatch | null {
  if (!groups) return null;
  const startHour = Number(groups.startHour);
  const endHour = Number(groups.endHour);
  if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) return null;
  return {
    startHour,
    startMinute: groups.startMinute ? Number(groups.startMinute) : undefined,
    startSuffix: groups.startSuffix,
    endHour,
    endMinute: groups.endMinute ? Number(groups.endMinute) : undefined,
    endSuffix: groups.endSuffix,
  };
}

const START_END_PATTERNS: Array<{ re: RegExp; parse: (m: RegExpMatchArray) => StartEndMatch | null }> = [
  // PT: das 13h às 16h / às 13h até às 16h / 13h-16h / 13:00 às 16:00
  {
    re: /\b(?:das|as|às|a|de)\s+(?<startHour>\d{1,2})(?:[:h.](?<startMinute>\d{2}))?\s*(?:h|hs|horas?)?\s*(?:a|à|ate|até|as|às|-)\s+(?:as|às|a|o)?\s*(?<endHour>\d{1,2})(?:[:h.](?<endMinute>\d{2}))?\s*(?:h|hs|horas?)?/u,
    parse: (m) => groupsToMatch(m.groups),
  },
  // EN: from 1pm to 4pm / 1pm-4pm
  {
    re: /\b(?:from)\s+(?<startHour>\d{1,2})(?:[:.](?<startMinute>\d{2}))?\s*(?<startSuffix>am|pm)?\s*(?:to|until|till|-)\s+(?<endHour>\d{1,2})(?:[:.](?<endMinute>\d{2}))?\s*(?<endSuffix>am|pm)?/u,
    parse: (m) => groupsToMatch(m.groups),
  },
  // ES: de 13h a 16h / desde las 13 hasta las 16
  {
    re: /\b(?:de|desde)\s+(?:las\s+)?(?<startHour>\d{1,2})(?:[:.](?<startMinute>\d{2}))?\s*(?<startSuffix>h|horas?|am|pm)?\s*(?:a|hasta|hacia|-)\s+(?:las\s+)?(?<endHour>\d{1,2})(?:[:.](?<endMinute>\d{2}))?\s*(?<endSuffix>h|horas?|am|pm)?/u,
    parse: (m) => groupsToMatch(m.groups),
  },
  // FR: de 13h à 16h
  {
    re: /\b(?:de)\s+(?<startHour>\d{1,2})(?:[:h.](?<startMinute>\d{2}))?\s*(?<startSuffix>h|heures?)?\s*(?:à|a|jusqu'à|-)\s+(?<endHour>\d{1,2})(?:[:h.](?<endMinute>\d{2}))?\s*(?<endSuffix>h|heures?)?/u,
    parse: (m) => groupsToMatch(m.groups),
  },
  // IT: dalle 13 alle 16
  {
    re: /\b(?:dalle|dalla|dalle\s+ore)\s+(?<startHour>\d{1,2})(?:[:.](?<startMinute>\d{2}))?\s*(?:alle|alla|-)\s+(?<endHour>\d{1,2})(?:[:.](?<endMinute>\d{2}))?/u,
    parse: (m) => groupsToMatch(m.groups),
  },
  // DE: von 13 bis 16 Uhr
  {
    re: /\b(?:von)\s+(?<startHour>\d{1,2})(?:[:.](?<startMinute>\d{2}))?\s*(?<startSuffix>uhr)?\s*(?:bis|zu|-)\s+(?<endHour>\d{1,2})(?:[:.](?<endMinute>\d{2}))?\s*(?<endSuffix>uhr)?/u,
    parse: (m) => groupsToMatch(m.groups),
  },
];

const UNTIL_ONLY_PATTERNS: Array<{ re: RegExp; hourGroup: number; minuteGroup: number; suffixGroup?: number }> = [
  { re: /\b(?:ate|até|às|as|a)\s+(?:as|às|a|o)?\s*(\d{1,2})(?:[:h.](\d{2}))?\s*(h|hs|horas?|pm|am)?/u, hourGroup: 1, minuteGroup: 2, suffixGroup: 3 },
  { re: /\b(?:until|till|by)\s+(\d{1,2})(?:[:.](\d{2}))?\s*(h|pm|am|o'?clock)?/u, hourGroup: 1, minuteGroup: 2, suffixGroup: 3 },
  { re: /\b(?:hasta)\s+(?:las\s+)?(\d{1,2})(?:[:.](\d{2}))?\s*(h|horas?|pm|am)?/u, hourGroup: 1, minuteGroup: 2, suffixGroup: 3 },
  { re: /\b(?:jusqu'?\s?à)\s+(?:l[ae]\s+)?(\d{1,2})(?:[:h.](\d{2}))?\s*(h|heures?)?/u, hourGroup: 1, minuteGroup: 2, suffixGroup: 3 },
  { re: /\b(?:fino\s+alle)\s+(\d{1,2})(?:[:.](\d{2}))?/u, hourGroup: 1, minuteGroup: 2 },
  { re: /\b(?:bis)\s+(?:um\s+)?(\d{1,2})(?:[:.](\d{2}))?\s*(uhr)?/u, hourGroup: 1, minuteGroup: 2, suffixGroup: 3 },
];

const NEXT_HOURS_PATTERNS: RegExp[] = [
  /\b(?:nas?\s+)?proxim[ao]s\s+(\d{1,2})(?:[.,](\d))?\s*horas?/u,
  /\b(?:in\s+the\s+)?next\s+(\d{1,2})(?:[.,](\d))?\s*hours?/u,
  /\b(?:en\s+las\s+)?proximas\s+(\d{1,2})\s*horas?/u,
  /\bprochaines\s+(\d{1,2})\s*heures?/u,
  /\bprossime\s+(\d{1,2})\s*ore/u,
  /\bnachsten\s+(\d{1,2})\s*stunden/u,
];

const TODAY_ONLY_PATTERNS: RegExp[] = [
  /\b(?:hoje|esta\s+tarde|esta\s+manha|esta\s+noite|agora)\b/u,
  /\b(?:today|this\s+afternoon|this\s+morning|this\s+evening|right\s+now|now)\b/u,
  /\b(?:hoy|esta\s+tarde|ahora)\b/u,
  /\b(?:aujourd'?hui|cet\s+apres[- ]midi|maintenant)\b/u,
  /\b(?:oggi|questo\s+pomeriggio|adesso|ora\s+fino)\b/u,
  /\b(?:heute|heute\s+nachmittag|jetzt)\b/u,
];

/** Pedido multi-dia explícito — anula o intradiário. */
const MULTI_DAY = /\b\d+\s*(?:dias?|days?|giorni|giorno|jours?|tage|tag|noites?|nights?|noches?|notti|nachte)\b/u;

const round = (hours: number) => Math.max(0.5, Math.round(hours * 2) / 2);

function normalizeHour(hour: number, suffix: string): number {
  let h = hour;
  const s = (suffix ?? "").toLowerCase();
  if (/pm/.test(s) && h < 12) h += 12;
  if (/am/.test(s) && h === 12) h = 0;
  return h;
}

function minutesOf(hour: number, minuteRaw: number | undefined): number {
  const minute = Number(minuteRaw ?? 0);
  if (!Number.isFinite(hour) || hour > 23 || !Number.isFinite(minute) || minute > 59) return -1;
  return hour * 60 + minute;
}

function localParts(date: Date, timeZone?: string): { hours: number; minutes: number } {
  if (!timeZone || typeof Intl === "undefined") {
    return { hours: date.getHours(), minutes: date.getMinutes() };
  }
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const hours = Number(get("hour"));
  const minutes = Number(get("minute"));
  return Number.isFinite(hours) && Number.isFinite(minutes)
    ? { hours, minutes }
    : { hours: date.getHours(), minutes: date.getMinutes() };
}

export function localNow(timeZone?: string): Date {
  if (!timeZone || typeof Intl === "undefined") return new Date();
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const iso = `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function detectDayOffset(value: string): number {
  for (const { offset, re } of DAY_OFFSET_PATTERNS) {
    if (re.test(value)) return offset;
  }
  if (TONIGHT_PATTERNS.some((re) => re.test(value))) return 0;
  return 0;
}

export function parseRelativeDayWindow(text: string, timeZone?: string, now?: Date): RelativeDayWindow | null {
  const value = fold(text);
  if (!value.trim()) return null;
  if (MULTI_DAY.test(value)) return null;

  const dayOffset = detectDayOffset(value);
  const reference = now ?? new Date();
  const { hours, minutes } = localParts(reference, timeZone);
  const nowMinutes = hours * 60 + minutes;

  for (const { re, parse } of START_END_PATTERNS) {
    const match = value.match(re);
    if (!match) continue;
    const g = parse(match);
    if (!g) continue;
    const startHour = normalizeHour(g.startHour, g.startSuffix ?? "");
    const startMin = minutesOf(startHour, g.startMinute);
    const endHour = normalizeHour(g.endHour, g.endSuffix ?? g.startSuffix ?? "");
    const endMin = minutesOf(endHour, g.endMinute);
    if (startMin < 0 || endMin < 0) continue;
    // Intervalo que atravessa a meia-noite: trata-se como duração positiva.
    const durationMin = endMin <= startMin ? endMin + 24 * 60 - startMin : endMin - startMin;
    const expired = dayOffset === 0 && endMin <= nowMinutes && durationMin < 24 * 60;
    return {
      intraday: true,
      dayOffset,
      startMinutes: startMin,
      endMinutes: endMin,
      durationHours: round(durationMin / 60),
      expired,
    };
  }

  for (const { re, hourGroup, minuteGroup, suffixGroup } of UNTIL_ONLY_PATTERNS) {
    const match = value.match(re);
    if (!match) continue;
    const suffix = suffixGroup ? match[suffixGroup] : undefined;
    const hour = normalizeHour(Number(match[hourGroup]), suffix ?? "");
    const endMin = minutesOf(hour, Number(match[minuteGroup] ?? 0) || undefined);
    if (endMin < 0) continue;
    const remaining = dayOffset === 0 ? endMin - nowMinutes : endMin + (dayOffset * 24 * 60 - nowMinutes);
    if (remaining <= 0) return { intraday: true, dayOffset, endMinutes: endMin, expired: true };
    return {
      intraday: true,
      dayOffset,
      endMinutes: endMin,
      durationHours: round(remaining / 60),
      expired: false,
    };
  }

  // "até à meia-noite" / "until midnight" — fim às 00:00 do dia relativo.
  if (/\b(?:ate|até|until|till|hasta|jusqu'[aà]|fino\s+a|bis)\s+(?:[àa]s?\s+)?(?:meia[- ]?noite|midnight|medianoche|minuit|mezzanotte|mitternacht)\b/u.test(value)) {
    const endMin = 0;
    const remaining = dayOffset === 0 ? endMin + 24 * 60 - nowMinutes : endMin + (dayOffset * 24 * 60 - nowMinutes);
    return {
      intraday: true,
      dayOffset,
      endMinutes: endMin,
      durationHours: round(remaining / 60),
      expired: false,
    };
  }

  for (const pattern of NEXT_HOURS_PATTERNS) {
    const match = value.match(pattern);
    if (!match) continue;
    const hours = Number(`${match[1]}${match[2] ? `.${match[2]}` : ""}`);
    if (!Number.isFinite(hours) || hours <= 0 || hours > 16) continue;
    return {
      intraday: true,
      dayOffset: 0,
      endMinutes: Math.min(24 * 60 - 1, nowMinutes + hours * 60),
      durationHours: round(hours),
      expired: false,
    };
  }

  if (TODAY_ONLY_PATTERNS.some((re) => re.test(value))) {
    return { intraday: true, dayOffset: 0, expired: false };
  }

  return null;
}
